import { twResolve } from "@/helpers/tw-resolve";

interface PaperBaseProps {
  "data-testid"?: string;
  children?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}

interface ContentProps {
  title?: string;
  renderTitle?: (title?: string) => React.ReactNode;
  subTitle?: React.ReactNode;
  actions?: React.ReactNode;
  header?: React.ReactNode;
  "data-testid"?: string;
}

type NewPaperProps = PaperBaseProps & ContentProps & { theme?: "minimal-constrained" | "constrained" | "wide" };
type MinimalPaperProps = PaperBaseProps & { theme: "no-header-minimal" };

export type PaperProps = NewPaperProps | MinimalPaperProps;

export function Paper(props: PaperProps): React.ReactNode {
  if (props.theme === "no-header-minimal") {
    return <MinimalPaperTheme {...props} />;
  }

  return <NewPaperTheme {...props} />;
}

function NewPaperTheme({
  children,
  renderTitle,
  title,
  subTitle,
  actions,
  "data-testid": dataTestid,
  header,
  theme,
  ref,
}: NewPaperProps): React.ReactNode {
  return (
    <section
      className="relative mx-auto flex min-h-screen max-w-screen-3xl flex-col gap-4 p-4 2xl:gap-6 2xl:p-6"
      ref={ref}
      data-testid={dataTestid}
    >
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-4 rounded-lg bg-white p-4 lg:gap-8">
        <div className="max-w-full">
          <h1 className="block max-w-full text-headline2 md:text-headline1">
            {renderTitle ? renderTitle(title) : title}
          </h1>
          {subTitle && <div className="max-w-[840px] text-caption text-grey-700">{subTitle}</div>}
        </div>
        {actions && <div className="flex flex-wrap gap-4 self-start sm:self-center">{actions}</div>}
      </header>

      {/* Action menu */}
      {header && <div className="overflow-y-visible rounded-lg bg-white p-4">{header}</div>}

      {/* Content */}
      <div
        className={twResolve(
          "rounded-lg bg-white p-4",
          theme === "constrained" && "max-w-[840px]",
          (!theme || theme === "minimal-constrained") && "bg-transparent p-0",
          theme === "minimal-constrained" && "max-w-[840px]",
        )}
      >
        {children}
      </div>
    </section>
  );
}

function MinimalPaperTheme({
  children,
  "data-testid": dataTestid,
  ref,
}: Omit<MinimalPaperProps, "theme">): React.ReactNode {
  return (
    <section ref={ref} data-testid={dataTestid} className="relative min-h-full p-4 2xl:p-6">
      {children}
    </section>
  );
}
